import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getConfig, listCuotas, listGastos, listAlumnos } from "../../lib/data";
import { formatCLP } from "../../lib/format";
import { MESES_ES, type Cuota, type Gasto, type EstadoCuota } from "../../lib/types";
import { ESTADO_CUOTA_LABEL } from "../../lib/types";
import { StatCard, Spinner } from "../../components/ui";

const ESTADO_COLOR: Record<EstadoCuota, string> = {
  pagado: "#10b981",
  pendiente: "#f43f5e",
  por_vencer: "#f59e0b",
  exento: "#94a3b8",
  beca: "#8b5cf6",
  abono: "#0ea5e9",
};

export function Dashboard() {
  const [ano] = useState(new Date().getFullYear());
  const [cuotas, setCuotas] = useState<Cuota[]>([]);
  const [gastos, setGastos] = useState<Gasto[]>([]);
  const [alumnosCount, setAlumnosCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        await getConfig();
        const [c, g, a] = await Promise.all([listCuotas(ano), listGastos(ano), listAlumnos()]);
        setCuotas(c);
        setGastos(g);
        setAlumnosCount(a.filter((x) => x.estado === "activo").length);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [ano]);

  const kpis = useMemo(() => {
    const recaudado = cuotas.filter((c) => c.estado === "pagado").reduce((s, c) => s + c.montoFinal, 0);
    const pendiente = cuotas
      .filter((c) => c.estado === "pendiente" || c.estado === "por_vencer" || c.estado === "abono")
      .reduce((s, c) => s + c.montoFinal, 0);
    const gastoTotal = gastos.reduce((s, g) => s + g.monto, 0);
    const morosos = cuotas.filter((c) => c.estado === "pendiente").length;
    return { recaudado, pendiente, gastoTotal, saldo: recaudado - gastoTotal, morosos };
  }, [cuotas, gastos]);

  const porMes = useMemo(() => {
    const map = new Map<number, { mes: string; recaudado: number; gasto: number }>();
    for (let m = 1; m <= 12; m++) map.set(m, { mes: MESES_ES[m - 1].slice(0, 3), recaudado: 0, gasto: 0 });
    for (const c of cuotas) {
      if (c.estado === "pagado") map.get(c.mes)!.recaudado += c.montoFinal;
    }
    for (const g of gastos) {
      const m = new Date(g.fecha).getMonth() + 1;
      if (map.has(m)) map.get(m)!.gasto += g.monto;
    }
    return [...map.values()];
  }, [cuotas, gastos]);

  const porEstado = useMemo(() => {
    const map = new Map<EstadoCuota, number>();
    for (const c of cuotas) map.set(c.estado, (map.get(c.estado) || 0) + 1);
    return [...map.entries()].map(([estado, value]) => ({
      estado,
      name: ESTADO_CUOTA_LABEL[estado],
      value,
    }));
  }, [cuotas]);

  if (loading) return <Spinner label="Cargando panel…" />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-800">Panel general · {ano}</h1>
        <p className="text-sm text-slate-500">Resumen de recaudación y gestión del dinero</p>
      </div>

      {error && (
        <div className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-700">
          No se pudieron cargar datos: {error}. ¿Ya configuraste Firebase (.env.local)?
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Recaudado" value={formatCLP(kpis.recaudado)} tone="emerald" />
        <StatCard label="Por cobrar" value={formatCLP(kpis.pendiente)} tone="amber" hint={`${kpis.morosos} morosas`} />
        <StatCard label="Gastos" value={formatCLP(kpis.gastoTotal)} tone="rose" />
        <StatCard label="Saldo en caja" value={formatCLP(kpis.saldo)} tone={kpis.saldo >= 0 ? "brand" : "rose"} />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="card p-4 lg:col-span-2">
          <h2 className="mb-3 font-bold text-slate-700">Ingresos vs. gastos por mes</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={porMes}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip formatter={(v: number) => formatCLP(v)} />
              <Legend />
              <Bar dataKey="recaudado" name="Recaudado" fill="#1b6ff5" radius={[6, 6, 0, 0]} />
              <Bar dataKey="gasto" name="Gasto" fill="#f43f5e" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card p-4">
          <h2 className="mb-3 font-bold text-slate-700">Estado de cuotas</h2>
          {porEstado.length === 0 ? (
            <p className="p-6 text-center text-sm text-slate-400">Aún no hay cuotas generadas.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={porEstado} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {porEstado.map((e) => (
                    <Cell key={e.estado} fill={ESTADO_COLOR[e.estado]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Alumnos activos" value={String(alumnosCount)} tone="slate" />
        <StatCard label="Cuotas del año" value={String(cuotas.length)} tone="slate" />
        <StatCard label="Cuotas pagadas" value={String(cuotas.filter((c) => c.estado === "pagado").length)} tone="slate" />
        <StatCard label="Registros de gasto" value={String(gastos.length)} tone="slate" />
      </div>
    </div>
  );
}
